import { m } from 'framer-motion';
import PropTypes from 'prop-types';

import { Link } from '@mui/material';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { ForbiddenIllustration } from 'src/assets/illustrations';
import { USER_ROLES_LABELS, SUBSCRIPTION_STATUS } from 'src/_mock';

import { varBounce, MotionContainer } from 'src/components/animate';

import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

export default function RoleBasedGuard({ hasContent, roles, children, isCreatePage, sx }) {
  const { user, currentTeam } = useAuthContext();

  // If the user is SUPER_ADMIN, grant access immediately
  if (user?.isSuperAdmin) {
    return <>{children}</>;
  }

  // Determine if the user has any of the required roles
  const currentRole = user?.role; // Global roles, e.g., [ADMIN, USER].
  const currentTeamRole = user?.currentTeamRole; // Team-specific roles, e.g., [DOCTOR, OWNER, MEMBER, NURSE, RECEPTIONIST, ACCOUNTANT].
  // Check if the user is subscribed
  const isUserNotSubscribed = currentRole === USER_ROLES_LABELS.USER && !currentTeamRole;
  // Check if team is subscribed
  const isTeamNotSubscribed = currentTeam?.subscription?.status === SUBSCRIPTION_STATUS.EXPIRED;
  //
  const isCreateSection = isCreatePage && user?.role === USER_ROLES_LABELS.ADMIN;
  //
  const hasPermission = roles?.some(
    (role) => currentTeamRole?.includes?.(role) || (currentRole?.includes?.(role) && currentTeamRole?.includes?.(role))
  );
  // Render "Permission Denied" UI if roles are defined and the user lacks the required roles
  if (isUserNotSubscribed || isTeamNotSubscribed || (roles?.length && !hasPermission && !isCreateSection)) {
    return hasContent ? (
      <Container component={MotionContainer} sx={{ textAlign: 'center', ...sx }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" sx={{ mb: 2, mt: 10 }}>
            Permission Denied
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <Typography sx={{ color: 'text.secondary' }}>
            {!currentTeamRole ? <>You will need a <Link component={RouterLink} href={paths.dashboard.organization.new}>team</Link> to access this page</> : 'You do not have permission to access this page'}
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <ForbiddenIllustration
            sx={{
              height: 260,
              my: { xs: 5, sm: 10 },
            }}
          />
        </m.div>
      </Container>
    ) : null;
  }

  // Render children if user has the required roles
  return <>{children}</>;
}

RoleBasedGuard.propTypes = {
  children: PropTypes.node,
  hasContent: PropTypes.bool,
  roles: PropTypes.arrayOf(PropTypes.string),
  sx: PropTypes.object,
  isCreatePage: PropTypes.bool,
};
